/**
 * computeMetrics.ts
 * Derives session metrics + dynamic tips from raw MediaPipe landmark frames.
 */

const LM = {
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW:    13, R_ELBOW:    14,
  L_WRIST:    15, R_WRIST:    16,
  L_HIP:      23, R_HIP:      24,
  L_KNEE:     25, R_KNEE:     26,
  L_ANKLE:    27, R_ANKLE:    28,
} as const;

export interface LandmarkPoint { x: number; y: number; z: number; visibility?: number; }
export interface LandmarkFrame { timestamp: number; landmarks: LandmarkPoint[]; }

export interface SessionMetrics {
  label: string; date: string;
  overall: number; smash: number; footwork: number; landing: number; prep: number;

  jumpHeight: number; hangTime: number; jumpCount: number;

  // Relative speed (pose-based). Not true km/h without real calibration.
  maxSpeed: number;
  avgSpeed: number;

  shots: number;
  duration: string;

  displacement: number;
  dirChanges: number;
  lungeCount: number;
  lungeDepth: number;

  recoveryTime: number;
  postureScore: number;

  backArch: number;
  kneeBend: number;
  stanceWidth: number;

  // Beta: can be noisy
  wristSpeed: number;
}

export type AnalysisEvents = {
  hitTimes: number[]; // seconds in video where likely hit/contact occurred
};

export interface Tip { type: "warn" | "good" | "info"; icon: string; title: string; desc: string; }

// ── Math helpers ──────────────────────────────────────────────────────────────
function angleDeg(a: LandmarkPoint, b: LandmarkPoint, c: LandmarkPoint): number {
  const v1 = { x: a.x - b.x, y: a.y - b.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const mag = Math.sqrt(v1.x**2 + v1.y**2) * Math.sqrt(v2.x**2 + v2.y**2);
  if (mag < 1e-9) return 0;
  return Math.acos(Math.max(-1, Math.min(1, dot / mag))) * (180 / Math.PI);
}

function dist(a: LandmarkPoint, b: LandmarkPoint) {
  return Math.sqrt((a.x-b.x)**2 + (a.y-b.y)**2);
}

function mid(a: LandmarkPoint, b: LandmarkPoint): LandmarkPoint {
  return { x:(a.x+b.x)/2, y:(a.y+b.y)/2, z:(a.z+b.z)/2 };
}

function mean(arr: number[]) {
  return arr.length ? arr.reduce((s,v) => s+v, 0) / arr.length : 0;
}

function stdDev(arr: number[], avg: number) {
  return arr.length < 2 ? 0 : Math.sqrt(arr.reduce((s,v) => s+(v-avg)**2, 0) / arr.length);
}

function percentile(sorted: number[], p: number) {
  return sorted.length ? sorted[Math.floor((p/100)*(sorted.length-1))] : 0;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function todayStr() {
  return new Date().toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
}

function formatDuration(s: number) {
  const m = Math.floor(s/60);
  const sec = Math.round(s%60);
  return m > 0 ? `${m} min ${sec}s` : `${sec}s`;
}

function lm(frame: LandmarkFrame, idx: number): LandmarkPoint | null {
  const pt = frame.landmarks?.[idx];
  if (!pt) return null;
  if (pt.visibility !== undefined && pt.visibility < 0.4) return null;
  return pt;
}

function smooth(arr: number[], window = 5) {
  if (arr.length < 3) return arr;
  const out: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    const a = Math.max(0, i - Math.floor(window / 2));
    const b = Math.min(arr.length - 1, i + Math.floor(window / 2));
    out.push(mean(arr.slice(a, b + 1)));
  }
  return out;
}

function estimateNormHeight(frame: LandmarkFrame): number | null {
  const nose = lm(frame, 0);
  const lA = lm(frame, LM.L_ANKLE);
  const rA = lm(frame, LM.R_ANKLE);
  if (!nose || !lA || !rA) return null;

  const ankleMid = mid(lA, rA);
  const h = Math.abs(ankleMid.y - nose.y);
  return h > 0.15 ? h : null;
}

// ── Main export ───────────────────────────────────────────────────────────────
export function computeMetrics(
  frames: LandmarkFrame[],
  duration: number,
  opts?: { playerHeightCm?: number }
): { session: SessionMetrics; tips: Tip[]; events: AnalysisEvents } {
  if (!frames || frames.length < 5) {
    return { session: defaultSession(duration), tips: defaultTips(), events: { hitTimes: [] } };
  }

  const fps = frames.length / Math.max(duration, 1);

  // scaling (rough)
  const playerHeightCm = opts?.playerHeightCm;
  const heights: number[] = [];
  for (const f of frames) {
    const h = estimateNormHeight(f);
    if (h) heights.push(h);
  }
  const avgNormHeight = mean(heights);
  const cmPerNorm =
    playerHeightCm && avgNormHeight > 0.2 ? (playerHeightCm / avgNormHeight) : 309;

  const hipY: number[] = [], hipX: number[] = [];
  const elbowAngles: number[] = [], kneeAngles: number[] = [];
  const backAngles: number[] = [], stanceWidths: number[] = [];
  const wristDisp: number[] = [];
  const wristTimes: number[] = [];

  for (let i = 0; i < frames.length; i++) {
    const f = frames[i];

    const lHip = lm(f, LM.L_HIP), rHip = lm(f, LM.R_HIP);
    if (lHip && rHip) {
      const hp = mid(lHip, rHip);
      hipY.push(hp.y);
      hipX.push(hp.x);
    }

    const lS = lm(f,LM.L_SHOULDER), lE = lm(f,LM.L_ELBOW), lW = lm(f,LM.L_WRIST);
    const rS = lm(f,LM.R_SHOULDER), rE = lm(f,LM.R_ELBOW), rW = lm(f,LM.R_WRIST);
    const lEl = lS&&lE&&lW ? angleDeg(lS,lE,lW) : 0;
    const rEl = rS&&rE&&rW ? angleDeg(rS,rE,rW) : 0;
    if (lEl || rEl) elbowAngles.push(Math.max(lEl, rEl));

    const lH2 = lm(f,LM.L_HIP), lK = lm(f,LM.L_KNEE), lA = lm(f,LM.L_ANKLE);
    const rH2 = lm(f,LM.R_HIP), rK = lm(f,LM.R_KNEE), rA = lm(f,LM.R_ANKLE);
    const lKn = lH2&&lK&&lA ? angleDeg(lH2,lK,lA) : 180;
    const rKn = rH2&&rK&&rA ? angleDeg(rH2,rK,rA) : 180;
    kneeAngles.push(Math.min(lKn, rKn));

    if (lS && rS && lHip && rHip) {
      const shMid = mid(lS, rS);
      const hpMid = mid(lHip, rHip);
      backAngles.push(
        Math.abs(
          Math.atan2(shMid.x - hpMid.x, Math.max(hpMid.y - shMid.y, 0.01)) * (180 / Math.PI)
        )
      );
    }

    const lAnk = lm(f,LM.L_ANKLE), rAnk = lm(f,LM.R_ANKLE);
    if (lAnk && rAnk) stanceWidths.push(Math.abs(lAnk.x - rAnk.x));

    if (i > 0) {
      const pf = frames[i-1];
      const plW = pf.landmarks?.[LM.L_WRIST], prW = pf.landmarks?.[LM.R_WRIST];
      const clW = f.landmarks?.[LM.L_WRIST],  crW = f.landmarks?.[LM.R_WRIST];
      const lSpd = plW&&clW ? dist(plW,clW) : 0;
      const rSpd = prW&&crW ? dist(prW,crW) : 0;
      wristDisp.push(Math.max(lSpd, rSpd) * fps);
      wristTimes.push(f.timestamp);
    }
  }

  const hipYSm = smooth(hipY, 7);
  const hipXSm = smooth(hipX, 7);

  // Jump metrics
  const sortedHipY = [...hipYSm].sort((a,b) => b-a);
  const hipBaseline = percentile(sortedHipY, 25);
  const maxJumpNorm = Math.max(0, hipBaseline - Math.min(...hipYSm));
  const jumpHeightCm = clamp(Math.round(maxJumpNorm * cmPerNorm), 0, 120);

  const jumpThreshold = hipBaseline - 0.025;
  let jumpCount = 0, inJump = false, totalJumpFrames = 0;
  for (const y of hipYSm) {
    if (y < jumpThreshold) {
      if (!inJump) { jumpCount++; inJump = true; }
      totalJumpFrames++;
    } else {
      inJump = false;
    }
  }
  const hangTime = parseFloat(
    clamp(jumpCount > 0 ? totalJumpFrames / fps / jumpCount : 0, 0, 1.2).toFixed(2)
  );

  // Body speed (relative)
  const bodyDisp: number[] = [];
  for (let i = 1; i < hipXSm.length; i++) {
    const dx = hipXSm[i]-hipXSm[i-1], dy = hipYSm[i]-hipYSm[i-1];
    bodyDisp.push(Math.sqrt(dx**2+dy**2) * fps);
  }
  const avgBodySpeed = mean(bodyDisp);
  const maxBodySpeed = Math.max(...bodyDisp, 0);

  const maxSpeedDisplay = clamp(Math.round(maxBodySpeed * cmPerNorm), 20, 260);
  const avgSpeedDisplay = clamp(Math.round(avgBodySpeed * cmPerNorm), 10, 180);
  const displacementCm  = clamp(
    Math.round(bodyDisp.reduce((s,v)=>s+v/fps,0) * cmPerNorm),
    0,
    5000
  );

  // Direction changes
  let dirChanges = 0;
  for (let i = 2; i < hipXSm.length; i++) {
    const pd = hipXSm[i-1]-hipXSm[i-2];
    const cd = hipXSm[i]-hipXSm[i-1];
    if (Math.abs(cd) > 0.003 && Math.sign(pd) !== Math.sign(cd)) dirChanges++;
  }

  // Hits (wrist peaks) + shots
  const wristSm = smooth(wristDisp, 5);
  const avgWrist = mean(wristSm);
  const sdWrist  = stdDev(wristSm, avgWrist);
  const shotThr  = avgWrist + sdWrist * 1.8;

  let shots = 0, prevAbove = false;
  const hitTimes: number[] = [];
  for (let i = 0; i < wristSm.length; i++) {
    const s = wristSm[i];
    const above = s > shotThr;
    if (above && !prevAbove) {
      shots++;
      const t = wristTimes[i] ?? (i / fps);
      if (!hitTimes.length || Math.abs(t - hitTimes[hitTimes.length - 1]) > 0.35) {
        hitTimes.push(t);
      }
    }
    prevAbove = above;
  }
  shots = Math.max(shots, 1);

  // Lunges
  const avgStance = mean(stanceWidths);
  const sdStance = stdDev(stanceWidths, avgStance);
  const lungeThr = avgStance + sdStance * 1.3;
  let lunges = 0, prevLunge = false;
  for (const w of stanceWidths) {
    const l = w > lungeThr;
    if (l && !prevLunge) lunges++;
    prevLunge = l;
  }

  const avgElbow = mean(elbowAngles);
  const avgBackArch = mean(backAngles);
  const sortedKnee = [...kneeAngles].sort((a,b)=>a-b);
  const deepKnee = percentile(sortedKnee, 10);

  const avgStancePct = clamp(Math.round(avgStance * 100), 10, 80);
  const maxWristSpeed = parseFloat((Math.max(...wristSm, 0) * 100).toFixed(1));

  // Form scores (heuristic)
  const smash    = clamp(Math.round(100 - Math.abs(avgElbow - 155) * 1.2), 40, 98);
  const dcPerSec = dirChanges / Math.max(duration, 1);
  const footwork = clamp(Math.round(50 + dcPerSec * 12 + (avgSpeedDisplay > 80 ? 10 : 0)), 40, 98);
  const landing  = clamp(Math.round(95 - Math.abs(deepKnee - 38) * 1.0), 40, 98);
  const prep     = clamp(Math.round(95 - avgBackArch * 1.8 - Math.max(0, avgStancePct - 60) * 0.5), 40, 98);
  const overall  = Math.round(smash*0.30 + footwork*0.25 + landing*0.20 + prep*0.25);

  const posture  = clamp(Math.round(95 - avgBackArch * 2.2), 40, 98);
  const recovery = parseFloat(clamp(1 / Math.max(avgBodySpeed * 8, 0.5), 0.5, 3.0).toFixed(1));

  const session: SessionMetrics = {
    label: "Session",
    date: todayStr(),

    overall, smash, footwork, landing, prep,

    jumpHeight: jumpHeightCm,
    hangTime,
    jumpCount,

    maxSpeed: maxSpeedDisplay,
    avgSpeed: avgSpeedDisplay,

    shots,
    duration: formatDuration(duration),

    displacement: displacementCm,
    dirChanges,
    lungeCount: lunges,
    lungeDepth: avgStancePct,

    recoveryTime: recovery,
    postureScore: posture,

    backArch: Math.round(avgBackArch),
    kneeBend: Math.round(deepKnee),
    stanceWidth: avgStancePct,
    wristSpeed: maxWristSpeed,
  };

  return {
    session,
    tips: buildTips(session, avgElbow, deepKnee, avgBackArch, hangTime),
    events: { hitTimes },
  };
}

function buildTips(s: SessionMetrics, avgElbow: number, deepKnee: number, backArch: number, hangTime: number): Tip[] {
  const tips: Tip[] = [];

  if (avgElbow < 130) {
    tips.push({
      type:"warn", icon:"⚠️", title:"Low elbow on smash",
      desc:`Average elbow angle is ${Math.round(avgElbow)}° — ideal is 150-165°. Raise your elbow higher before contact for more power.`
    });
  } else if (avgElbow >= 150 && avgElbow <= 168) {
    tips.push({
      type:"good", icon:"✅", title:"Good smash elbow angle",
      desc:`Elbow at ${Math.round(avgElbow)}° is in the ideal range (150-165°). Maintain this consistently.`
    });
  } else {
    tips.push({
      type:"info", icon:"💡", title:"Elbow slightly overextended",
      desc:`Elbow angle of ${Math.round(avgElbow)}° is slightly above ideal. A small flex improves control.`
    });
  }

  if (hangTime > 0.45) {
    tips.push({
      type:"good", icon:"✅", title:"Strong jump timing",
      desc:`Hang time of ${hangTime}s is above average. Jump preparation is consistent — keep it up.`
    });
  } else if (s.jumpHeight > 10) {
    tips.push({
      type:"info", icon:"💡", title:"Extend your jump peak",
      desc:`Jump height is ${s.jumpHeight}cm but hang time is short at ${hangTime}s. Focus on full arm extension at peak.`
    });
  }

  if (deepKnee > 55) {
    tips.push({
      type:"warn", icon:"⚠️", title:"Insufficient knee bend",
      desc:`Deepest knee angle is ${Math.round(deepKnee)}°. Aim for 30-45° on lunges for better stability.`
    });
  } else if (deepKnee >= 28 && deepKnee <= 50) {
    tips.push({
      type:"good", icon:"✅", title:"Good landing mechanics",
      desc:`Knee bend of ${Math.round(deepKnee)}° on landings is ideal — joints absorbing impact safely.`
    });
  }

  if (backArch > 20) {
    tips.push({
      type:"warn", icon:"⚠️", title:"Excessive back lean",
      desc:`Back arch averages ${Math.round(backArch)}° from vertical. Engage your core to stay more upright.`
    });
  }

  if (s.recoveryTime > 1.8) {
    tips.push({
      type:"info", icon:"💡", title:"Improve recovery speed",
      desc:`Recovery time is ~${s.recoveryTime}s. Practise split-step + return to base after each shot.`
    });
  }

  if (s.dirChanges < 20) {
    tips.push({
      type:"info", icon:"💡", title:"Increase court coverage",
      desc:`Only ${s.dirChanges} direction changes detected. Split-step drills will improve court reach significantly.`
    });
  } else if (s.dirChanges > 60) {
    tips.push({
      type:"good", icon:"✅", title:"Active footwork",
      desc:`${s.dirChanges} direction changes shows strong court movement — maintain this through longer rallies.`
    });
  }

  if (tips.length < 3) {
    tips.push({
      type:"info", icon:"💡", title:"Keep logging sessions",
      desc:"Upload more sessions to track improvement across form, jump, and movement."
    });
  }

  return tips.slice(0, 5);
}

function defaultSession(duration: number): SessionMetrics {
  return {
    label:"Session", date:todayStr(),
    overall:0, smash:0, footwork:0, landing:0, prep:0,
    jumpHeight:0, hangTime:0, jumpCount:0,
    maxSpeed:0, avgSpeed:0, shots:0, duration:formatDuration(duration),
    displacement:0, dirChanges:0, lungeCount:0, lungeDepth:0,
    recoveryTime:0, postureScore:0,
    backArch:0, kneeBend:0, stanceWidth:0, wristSpeed:0,
  };
}

function defaultTips(): Tip[] {
  return [{
    type:"info",
    icon:"💡",
    title:"No pose data detected",
    desc:"MediaPipe could not detect a pose. Make sure the player is clearly visible in the frame and try again."
  }];
}